// Company branding (logo) for the signed-in admin's tenant.
// Upload goes to object storage (employee-service); the URL is persisted on the
// tenant (auth-service). Both are scoped to the caller's tenant via the JWT.
import axios from 'axios';

// All calls route through the API gateway (P0-4). The gateway maps /api/v1/tenant
// (singular) to employee-service and /api/v1/payroll to payroll-service.
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

function authHeader() {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Upload a logo image to object storage; returns its public URL. */
export async function uploadTenantLogo(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await axios.post(`${API_URL}/tenant/logo`, form, { headers: { ...authHeader() } });
  return data.url as string;
}

/** Persist the logo URL on the caller's tenant. */
export async function saveTenantLogo(logoUrl: string): Promise<void> {
  await axios.put(`${API_URL}/tenants/logo`, { logoUrl }, { headers: { ...authHeader() } });
}

/**
 * Mirror the tenant logo onto the payroll service so payslip PDFs use it
 * (payroll fetches + embeds the URL when rendering). Best-effort — a failure
 * here must not block saving the company logo.
 */
export async function syncPayrollBrandingLogo(logoUrl: string): Promise<void> {
  await axios.put(`${API_URL}/payroll/branding`, { logo_url: logoUrl }, { headers: { ...authHeader() } });
}

/** Read the current tenant (incl. logoUrl) by workspace slug. */
export async function getTenantBranding(slug: string): Promise<{ name?: string; logoUrl?: string } | null> {
  try {
    const { data } = await axios.get(`${API_URL}/tenants/workspace/${slug}/validate`);
    if (!data?.valid) return null;
    return { name: data.tenant?.name, logoUrl: data.tenant?.logoUrl };
  } catch {
    return null;
  }
}
